/**
 * AccountDialog Component
 * 
 * Dialog for creating and editing accounts with support for account details.
 */

import React from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DuplicateDetectionTextField from "../../../components/DuplicateDetectionTextField";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";
import InfoTooltipIcon from "../../../components/InfoTooltipIcon";
import { Account, Currency } from "../../../types";
import { AccountGroup } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsUIStateReturn } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsHelpersReturn } from "../hooks/useAccountSettingsHelpers";
import { UseAccountSettingsHandlersReturn } from "../hooks/useAccountSettingsHandlers";

export interface AccountDialogProps {
  open: boolean;
  onClose: () => void;
  uiState: UseAccountSettingsUIStateReturn;
  handlers: UseAccountSettingsHandlersReturn;
  helpers: UseAccountSettingsHelpersReturn;
  groups: AccountGroup[];
  accounts: Account[];
  currencies: Currency[];
  defaultCurrency: string;
}

/**
 * Account dialog component for creating and editing accounts
 */
export default function AccountDialog({
  open,
  onClose,
  uiState,
  handlers,
  helpers,
  groups,
  accounts,
  currencies,
  defaultCurrency,
}: AccountDialogProps): React.ReactElement {
  const handleClose = (): void => {
    uiState.setEditAccountDialogOpen(false);
    uiState.setEditingAccount(null);
    uiState.setAccountFormData({
      accountName: "",
      group: "",
      amount: "",
      currency: "USD",
      description: "",
      details: {},
    });
    uiState.setInitialAccountFormData(null);
    uiState.setShowAccountDetails(false);
    uiState.setAccountNameError("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {uiState.editingAccount ? "Edit Account" : "Create Account"}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handlers.handleAccountSubmit} id="edit-account-form">
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Group</InputLabel>
              <Select
                value={uiState.accountFormData.group}
                label="Group"
                onChange={(e) => {
                  const newGroup = e.target.value;
                  // Clear details and uncheck checkbox when group changes
                  uiState.setAccountFormData({ ...uiState.accountFormData, group: newGroup, details: {} });
                  uiState.setShowAccountDetails(false);
                  // Update initial state if editing to reflect the change
                  if (uiState.editingAccount && uiState.initialAccountFormData) {
                    uiState.setInitialShowAccountDetails(false);
                  }
                }}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.name}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <DuplicateDetectionTextField
              required
              label="Account Name"
              value={uiState.accountFormData.accountName}
              onChange={(value) => {
                uiState.setAccountFormData({ ...uiState.accountFormData, accountName: value });
              }}
              existingItems={accounts
                .filter((acc) => {
                  // When creating, exclude the account being created from duplicate check
                  if (uiState.isSavingAccount && uiState.creatingAccountName && !uiState.editingAccount) {
                    return acc.accountName.toLowerCase() !== uiState.creatingAccountName.toLowerCase();
                  }
                  return true;
                })
                .map((acc) => ({ name: acc.accountName, id: acc.id }))}
              currentItemId={uiState.editingAccount?.id}
              itemType="account"
              onErrorChange={(error) => {
                // Don't update error if we're in the process of saving (to prevent false positives)
                if (!uiState.isSavingAccount) {
                  uiState.setAccountNameError(error);
                }
              }}
              fullWidth
              placeholder="Enter account name"
            />

            <TextField
              label="Initial Amount"
              type="number"
              inputProps={{ step: "any" }}
              value={uiState.accountFormData.amount}
              onChange={(e) =>
                uiState.setAccountFormData({ ...uiState.accountFormData, amount: e.target.value })
              }
              fullWidth
              placeholder="0.00"
              helperText="Optional - defaults to 0 if not specified"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InfoTooltipIcon
                      tooltipMessage="You can still change the amount manually from here, but it will appear in activity and can't delete later."
                      position="inline"
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth required sx={{ position: "relative" }}>
              <InputLabel htmlFor="currency-select-settings">Currency</InputLabel>
              <Select
                id="currency-select-settings"
                value={uiState.accountFormData.currency}
                label="Currency"
                onChange={(e) =>
                  uiState.setAccountFormData({ ...uiState.accountFormData, currency: e.target.value })
                }
                disabled={!!uiState.editingAccount}
                sx={{
                  "& .MuiSelect-select": {
                    pr: 7,
                  },
                }}
              >
                <MenuItem value={defaultCurrency}>
                  {defaultCurrency}
                </MenuItem>
                {currencies
                  .filter((c) => c.code !== defaultCurrency && c.rate !== null)
                  .map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </MenuItem>
                  ))}
              </Select>
              <CannotChangeLaterIcon
                tooltipMessage="Currency cannot be changed after creation. You can define currencies and rates in Settings."
              />
            </FormControl>

            <TextField
              label="Description (Optional)"
              value={uiState.accountFormData.description}
              onChange={(e) =>
                uiState.setAccountFormData({ ...uiState.accountFormData, description: e.target.value })
              }
              fullWidth
              placeholder="Add a description..."
            />

            {/* Checkbox to show/hide Account Details */}
            {uiState.accountFormData.group && helpers.getSelectedGroupDetails().length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uiState.showAccountDetails}
                    onChange={(e) => {
                      uiState.setShowAccountDetails(e.target.checked);
                      // Don't clear details when unchecked - just hide the fields
                      // Details will be preserved until form is submitted or dialog is closed
                    }}
                  />
                }
                label="Fill Account Details"
              />
            )}

            {/* Detail Fields - Show only when checkbox is checked and group has details */}
            {uiState.accountFormData.group && helpers.getSelectedGroupDetails().length > 0 && uiState.showAccountDetails && (
              <Box
                sx={{
                  maxHeight: helpers.getSelectedGroupDetails().length >= 3 ? "240px" : "none",
                  overflowY: helpers.getSelectedGroupDetails().length >= 3 ? "auto" : "visible",
                  overflowX: "hidden",
                  pr: helpers.getSelectedGroupDetails().length >= 3 ? 1 : 0,
                  mt: 1
                }}
              >
                <Stack spacing={2} sx={{ pt: 1.5 }}>
                  {helpers.getSelectedGroupDetails().map((detail) => {
                    const currentValue = uiState.accountFormData.details?.[detail.name] || "";
                    return (
                      <Box key={detail.name} sx={{ position: "relative" }}>
                        {detail.type === "list" ? (
                          // List type: Show dropdown with list items
                          <FormControl fullWidth>
                            <InputLabel>{detail.name}</InputLabel>
                            <Select
                              value={currentValue || ""}
                              label={detail.name}
                              onChange={(e) => {
                                handlers.handleDetailValueChange(detail.name, e.target.value);
                              }}
                            >
                              {detail.listItems && detail.listItems.length > 0 ? (
                                detail.listItems.map((item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                ))
                              ) : (
                                <MenuItem value="" disabled>
                                  No items available
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        ) : detail.type === "number" ? (
                          // Number type: Only allow numeric input
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onKeyDown={(e) => {
                              // Prevent non-numeric characters from being typed
                              const key = e.key;
                              // Allow: numbers, backspace, delete, tab, escape, enter, and special keys (arrows, home, end, etc.)
                              if (
                                key === "Backspace" ||
                                key === "Delete" ||
                                key === "Tab" ||
                                key === "Escape" ||
                                key === "Enter" ||
                                key === "ArrowLeft" ||
                                key === "ArrowRight" ||
                                key === "ArrowUp" ||
                                key === "ArrowDown" ||
                                key === "Home" ||
                                key === "End" ||
                                e.ctrlKey ||
                                e.metaKey ||
                                e.altKey
                              ) {
                                return; // Allow these keys
                              }
                              // Allow decimal point only if not already present
                              if (key === "." || key === ",") {
                                const currentValueStr = String(currentValue || "");
                                if (currentValueStr.includes(".") || currentValueStr.includes(",")) {
                                  e.preventDefault();
                                }
                                return; // Allow one decimal point
                              }
                              // Allow minus sign only at the beginning
                              if (key === "-") {
                                const currentValueStr = String(currentValue || "");
                                if (currentValueStr.length > 0) {
                                  e.preventDefault();
                                }
                                return;
                              }
                              // Only allow numeric characters
                              if (!/^\d$/.test(key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Remove any non-numeric characters except decimal point and minus
                              const cleanedValue = value.replace(/[^\d.-]/g, "");
                              // Ensure minus is only at the start
                              let processedValue = cleanedValue;
                              if (cleanedValue.includes("-") && !cleanedValue.startsWith("-")) {
                                processedValue = cleanedValue.replace(/-/g, "");
                                if (processedValue && !processedValue.startsWith("-")) {
                                  processedValue = "-" + processedValue;
                                }
                              }
                              // Limit to one decimal point
                              const parts = processedValue.split(".");
                              if (parts.length > 2) {
                                processedValue = parts[0] + "." + parts.slice(1).join("");
                              }
                              // Also replace comma with period for decimal
                              processedValue = processedValue.replace(",", ".");
                              
                              if (processedValue === "" || processedValue === "-" || processedValue === ".") {
                                handlers.handleDetailValueChange(detail.name, processedValue);
                              } else {
                                const numValue = parseFloat(processedValue);
                                if (!isNaN(numValue) && isFinite(numValue)) {
                                  handlers.handleDetailValueChange(detail.name, numValue);
                                } else if (processedValue === "") {
                                  handlers.handleDetailValueChange(detail.name, "");
                                }
                              }
                            }}
                            type="text"
                            inputProps={{ 
                              inputMode: "decimal",
                              pattern: "[0-9.-]*"
                            }}
                            fullWidth
                            required={false}
                            disabled={false}
                          />
                        ) : (
                          // String or Text type: Single-line text input (same height as other fields)
                          <TextField
                            label={detail.name}
                            value={currentValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              let processedValue: string = value;
                              
                              // For both string and text types, use single-line (no newlines)
                              // This keeps the field the same height as other fields
                              processedValue = value.replace(/\n/g, "");
                              
                              handlers.handleDetailValueChange(detail.name, processedValue);
                            }}
                            fullWidth
                            required={false}
                            disabled={false}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          form="edit-account-form" 
          variant="contained"
          disabled={uiState.editingAccount ? !helpers.hasAccountFormChanged() : false}
          sx={{ flex: 1, py: 1.5 }}
        >
          {uiState.editingAccount ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
